import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfFormPanel,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfAlertBar,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
  FrVrfTextDisplayField,
  FrVrfAccordion,
  FrVrfFileUploadField,
  FrVrfHorizontalSeparator,
} from "@vrf/fr-vrf-web-components-react";
import { setBelongingInformation } from "@vrf/fr-vrf-auth-core";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import { useGetProject } from "../../services/project";
import { useGetActionItems } from "../../services/screen-authorization/ScreenAuthorizationHook";
import {
  CreateProjectPlanTicketRequest,
  useCreateProjectPlanTicket,
} from "../../services/tickets";
import { UpdateTicketRequest, useUpdateTicket } from "../../services/ticket";
import { useGetLoginUserRoleList } from "../../services/data-list";
import {
  InDefCommonFlagType,
  PjtType,
  RoleCvrgType,
  RoleType,
} from "../../util/general-code/enums/general-codes";
import {
  ConfirmationWithCommentDialogContent,
  useConfirmationWithCommentDialogState,
} from "../dialog/confirmation-with-comment-dialog";
import {
  InputProjectInformationDialogContent,
  useInputProjectInformationDialogState,
} from "../dialog/input-project-information-dialog";
import {
  InputCostEstimationDialogContent,
  useInputCostEstimationDialogState,
} from "../dialog/input-cost-estimation-dialog";
import {
  InputComponentRequirementDialogContent,
  useInputComponentRequirementDialogState,
} from "../dialog/input-component-requirement-dialog";
import {
  LinkComponentProjectDialogContent,
  useLinkComponentProjectDialogState,
} from "../dialog/link-component-project-dialog";
import { useInputStakeholderDialogState } from "../dialog/input-stakeholder-dialog";
import {
  DisplayFilterType,
  ProjectStakeholderListContent,
  ProjectStakeholderListInitialState,
  SpreadsheetRecordData,
  useProjectStakeholderListState,
} from "../dialog/project-stakeholder-list";
import { ProjectPlanActionList } from "../../componentns/project-plan-action-list";
import { ProjectPlanStatusPanel } from "../../componentns/project-plan-status-panel";
import { usePageState, UpdStsReq, CrtPjtTcktReq } from ".";
import { ENV } from "@vrf/fr-vrf-core";
import { UploadRequirementsSheetRequest } from "../../services/project/upload_requirements_sheet/UploadRequirementsSheetRequest";
import { DownloadRequirementsSheetRequest } from "../../services/project/download_requirements_sheet/DownloadRequirementsSheetRequest";
import {
  useUploadRequirementsSheet,
  useDownloadRequirementsSheet,
} from "../../services/project";
import {
  MemoEditDialogContent,
  MemoType,
  useMemoEditDialogState,
} from "../dialog/memo-edit-dialog";

export enum DialogType {
  Confirm = "1",
  ConfirmWithComment = "2",
  Judgement = "3",
}

const TEAM_CODE_PROJECT = "PROJECT";
const VRF_SUCCESS_COLOR = "#1b8221";
const VRF_HORIZONTAL_SEPARATOR_COLOR = "#e6e6e6";
const MESSAGE_FOR_CHANGE_STATUS_WITHOUT_COMMENT = "(No comment)";

export const ProjectPlanPageContent = () => {
  const { showToast, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [authdActionItems, sendGetActionItems] = useGetActionItems();
  const [getProjectResponse, getProjectRequest] = useGetProject();
  const [createProjectPlanTicketResponse, createProjectTicketPlanRequest] =
    useCreateProjectPlanTicket();
  const [updateTicketResponse, updateTicketRequest] = useUpdateTicket();
  const [getLoginUserRoleListResponse, getLoginUserRoleListRequest] =
    useGetLoginUserRoleList();
  const [appState, setAppState] = useAppState();
  const [
    confirmationWithCommentDialogState,
    setConfirmationWithCommentDialogState,
  ] = useConfirmationWithCommentDialogState();
  const [
    inputProjectInformationDialogState,
    setInputProjectInformationDialogState,
  ] = useInputProjectInformationDialogState();
  const [inputCostEstimationDialogState, setInputCostEstimationDialogState] =
    useInputCostEstimationDialogState();
  const [
    inputComponentRequirementDialogState,
    setInputComponentRequirementDialogState,
  ] = useInputComponentRequirementDialogState();
  const [linkComponentProjectDialogState, setLinkComponentProjectDialogState] =
    useLinkComponentProjectDialogState();
  const [projectStakeholderListState, setProjectStakeholderListState] =
    useProjectStakeholderListState();
  const [inputStakeholderDialogState, setInputStakeholderDialogState] =
    useInputStakeholderDialogState();
  const [pageState, setPageState] = usePageState();
  const [memoEditDialogState, setMemoEditDialogState] =
    useMemoEditDialogState();

  const dfnSetVerNum = ENV("REACT_APP_DFN_SET_VER_NUM");
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    pjtId: queryParameter.get("pjtId") ?? "",
  };
  const [uploadRequirementsSheetResponse, uploadRequirementsSheetRequest] =
    useUploadRequirementsSheet();
  const [, downloadRequirementsSheetRequest] = useDownloadRequirementsSheet();

  const onInitialDisplayed = (msg?: string): void => {
    if (params.pjtId !== "") {
      getLoginUserRoleListRequest({ pjtId: params.pjtId });

      getProjectRequest({
        pjtId: params.pjtId,
        dfnSetVerNum: dfnSetVerNum,
      });
    } else {
      getLoginUserRoleListRequest({});
    }

    setAppState({
      ...appState,
      title:
        params.pjtId !== "" ? "Project Plan" : "Project Plan ( New Project )",
      pjtId: params.pjtId,
    });

    setPageState((prevState) => ({
      ...prevState,
      infoBar: {
        isShow: msg ? true : false,
        message: msg ?? "",
        ready: "",
      },
      authorized: params.pjtId === "" ? true : prevState.authorized,
    }));
  };
  useEffect(onInitialDisplayed, []);

  const onListenGetProjectResponse = (): void => {
    console.log("=------------ Get Response =------------------");
    console.log(getProjectResponse);

    if (getProjectResponse === undefined) return;

    if (getProjectResponse.planStatus !== null) {
      setBelongingInformation({
        belongTypeCode: RoleCvrgType.PROJECT,
        belongCode: !!params.pjtId ? params.pjtId : appState.pjtId,
      });

      sendGetActionItems({
        blngTypeCd: RoleCvrgType.PROJECT,
        blngCd: !!params.pjtId ? params.pjtId : appState.pjtId,
      });
    }

    setPageState((prevState) => ({
      ...prevState,
      result: {
        ...getProjectResponse,
        projectInformation: {
          ...getProjectResponse.projectInformation,
          chnTypeGrpTypeCdList:
            getProjectResponse.projectInformation.chnTypeGrpTypeCdList ?? [],
        },
        componentRequirementList:
          getProjectResponse.componentRequirementList ?? [],
        costEstimation: getProjectResponse.costEstimation,
        componentProjectList: getProjectResponse.componentProjectList ?? [],
        stakeholderList: getProjectResponse.stakeholderList ?? [],
        invalidRoleList: getProjectResponse.invalidRoleList ?? [],
      },
      authorized:
        getProjectResponse.planStatus === null ? true : prevState.authorized,
    }));

    const pageName = `Project Name: ${getProjectResponse.projectInformation.pjtName}`;
    setAppState({
      ...appState,
      title: `Project Plan ( ${pageName} )`,
    });
  };
  useEffect(onListenGetProjectResponse, [getProjectResponse]);

  const onListenGetLoginUserRoleListResponse = (): void => {
    if (getLoginUserRoleListResponse === undefined) return;

    setPageState((prevState) => ({
      ...prevState,
      loginUserRoleList: getLoginUserRoleListResponse.userRoleList,
    }));
  };
  useEffect(onListenGetLoginUserRoleListResponse, [
    getLoginUserRoleListResponse,
  ]);

  const onListenGetAuthdActionItemsResponse = () => {
    if (!authdActionItems) return;
    setPageState((prevState) => ({
      ...prevState,
      authorized: true,
    }));
  };
  useEffect(onListenGetAuthdActionItemsResponse, [authdActionItems]);

  /**
   * Project Information Dialog.
   */
  const showProjectInformationDialog = () => {
    const ediUsage =
      pageState.result.projectInformation.chnTypeGrpTypeCdList === undefined
        ? ""
        : pageState.result.projectInformation.chnTypeGrpTypeCdList.length !== 0
          ? "Use"
          : "DoNotUse";

    setInputProjectInformationDialogState({
      ...inputProjectInformationDialogState,
      saved: pageState.result.projectInformation,
      edited: pageState.result.projectInformation,
      pjtId: params.pjtId,
      status: {
        ...inputProjectInformationDialogState.status,
        ediUsage: ediUsage,
      },
      updateDateTime: pageState.result.updateDateTime,
      isShow: true,
    });
  };

  const onListenCloseProjectInformationDialog = (): void => {
    if (inputProjectInformationDialogState === undefined) return;
    if (
      !inputProjectInformationDialogState.isShow &&
      inputProjectInformationDialogState.status.isSaved
    ) {
      if (inputProjectInformationDialogState.isCreate) {
        setPageState((prevState) => ({
          ...prevState,
          authorized: false,
        }));
      }
      onInitialDisplayed();
    }
  };
  useEffect(onListenCloseProjectInformationDialog, [
    inputProjectInformationDialogState.isShow,
  ]);

  /**
   * Cost Estimation Dialog.
   */
  const showCostEstimationDialog = () => {
    setInputCostEstimationDialogState({
      ...inputCostEstimationDialogState,
      saved: pageState.result.costEstimation,
      edited: pageState.result.costEstimation,
      pjtId: params.pjtId,
      updateDateTime: pageState.result.updateDateTime,
      isShow: true,
    });
  };

  const onListenCloseCostEstimationDialog = (): void => {
    if (inputCostEstimationDialogState === undefined) return;
    if (
      inputCostEstimationDialogState.isShow === false &&
      inputCostEstimationDialogState.status.isSaved === true
    )
      onInitialDisplayed();
  };
  useEffect(onListenCloseCostEstimationDialog, [
    inputCostEstimationDialogState.isShow,
  ]);

  /**
   * Component Requirement Dialog.
   */
  const showComponentRequirementDialog = () => {
    const reqType = !pageState.result.tstUseTypeCd
      ? ""
      : pageState.result.componentRequirementList?.length !== 0
        ? "Both"
        : "OnlyIf";

    setInputComponentRequirementDialogState({
      ...inputComponentRequirementDialogState,
      saved: {
        reqType: reqType,
        tstUseTypeCd: pageState.result.tstUseTypeCd,
        componentRequirementList: pageState.result.componentRequirementList,
      },
      edited: {
        reqType: reqType,
        tstUseTypeCd: pageState.result.tstUseTypeCd,
        componentRequirementList: pageState.result.componentRequirementList,
      },
      pjtId: params.pjtId,
      updateDateTime: pageState.result.updateDateTime,
      isShow: true,
    });
  };

  const onListenCloseComponentRequirementDialog = (): void => {
    if (inputComponentRequirementDialogState === undefined) return;
    if (
      inputComponentRequirementDialogState.isShow === false &&
      inputComponentRequirementDialogState.status.isSaved === true
    )
      onInitialDisplayed();
  };
  useEffect(onListenCloseComponentRequirementDialog, [
    inputComponentRequirementDialogState.isShow,
  ]);

  /**
   * Component Project Dialog.
   */
  const showComponentProjectDialog = () => {
    setLinkComponentProjectDialogState({
      ...linkComponentProjectDialogState,
      saved: {
        componentRequirementList: pageState.result.componentRequirementList,
        cmpntPrjList: pageState.result.componentProjectList,
      },
      edited: {
        componentRequirementList: pageState.result.componentRequirementList,
        cmpntPrjList: pageState.result.componentProjectList,
      },
      pjtId: params.pjtId,
      updateDateTime: pageState.result.updateDateTime,
      isShow: true,
    });
  };

  const onListenCloseComponentProjectDialog = (): void => {
    if (linkComponentProjectDialogState === undefined) return;
    if (
      linkComponentProjectDialogState.isShow === false &&
      linkComponentProjectDialogState.status.isSaved === true
    )
      onInitialDisplayed();
  };
  useEffect(onListenCloseComponentProjectDialog, [
    linkComponentProjectDialogState.isShow,
  ]);

  /** Memo Edit Dialog */
  const showMemoEditDialog = () => {
    console.log("---- showMemoEditDialog Edit Start ------");
    setMemoEditDialogState((prevState) => ({
      ...prevState,
      isShow: true,
      pjtId: params.pjtId,
      mmTxt: pageState.result.mmTxt,
      updateDateTime: pageState.result.mmTxtUpdateDateTime,
      targetIndex: "",
      memoType: MemoType.PROJECT,
    }));
    console.log("---- showMemoEditDialog Edit End ------");
  };

  const onListenCloseMemoEditDialog = (): void => {
    if (memoEditDialogState === undefined) return;
    if (memoEditDialogState.isShow === false) {
      const editedMemo = memoEditDialogState.mmTxt;
      const editedMemoUpdateDateTime = memoEditDialogState.updateDateTime;
      setPageState((prevState) => ({
        ...prevState,
        result: {
          ...prevState.result,
          mmTxt: editedMemo,
          mmTxtUpdateDateTime: editedMemoUpdateDateTime,
        },
      }));
    }
  };
  useEffect(onListenCloseMemoEditDialog, [memoEditDialogState.isShow]);

  /**
   * Project Stakeholder List.
   */
  const getDisplayFilter = () => {
    const hasDipRole = pageState.loginUserRoleList.some((e) =>
      [
        RoleType.DIP_MANAGER,
        RoleType.DIP_APPROVER,
        RoleType.PIC_OF_DIP_APP,
        RoleType.PIC_OF_DIP_INFRA,
        RoleType.PIC_OF_IF_RD,
      ].includes(e.roleCd as RoleType),
    );
    const hasPjtRole = pageState.loginUserRoleList.some((e) =>
      [
        RoleType.PROJECT_OWNER,
        RoleType.PROJECT_APPROVER,
        RoleType.PIC_OF_COMPONENT_APP,
        RoleType.PIC_OF_COMPONENT_INFRA,
      ].includes(e.roleCd as RoleType),
    );

    return hasDipRole
      ? hasPjtRole
        ? DisplayFilterType.All
        : DisplayFilterType.Dip
      : hasPjtRole
        ? DisplayFilterType.Project
        : DisplayFilterType.All;
  };

  const showProjectStakeholderList = () => {
    setProjectStakeholderListState({
      ...projectStakeholderListState,
      saved: pageState.result.stakeholderList,
      edited: pageState.result.stakeholderList,
      invalidRoleList: pageState.result.invalidRoleList,
      displayFilter: getDisplayFilter(),
      fromActionList: false,
      pjtId: params.pjtId,
      pjtTypeCd: PjtType.INTERFACE,
      updateDateTime: pageState.result.updateDateTime,
      isShow: true,
    });
  };

  const showAndAddProjectStakeholderList = (roleCd: string, teamCd: string) => {
    const rowIdx = pageState.result.stakeholderList.length + 1;

    const addSpreadsheetRow: SpreadsheetRecordData = {
      ...ProjectStakeholderListInitialState.spreadsheet.tmplateRow,
      roleCd: roleCd,
      teamCd: teamCd,
      primFlg: pageState.result.stakeholderList.some(
        (e) => e.roleCd === roleCd && e.teamCd === teamCd,
      )
        ? InDefCommonFlagType.NO
        : InDefCommonFlagType.YES,
    };

    setProjectStakeholderListState({
      ...projectStakeholderListState,
      saved: pageState.result.stakeholderList,
      edited: pageState.result.stakeholderList,
      displayFilter: getDisplayFilter(),
      fromActionList: true,
      fromActionListParam: {
        primFlg: pageState.result.stakeholderList.some(
          (e) => e.roleCd === roleCd && e.teamCd === teamCd,
        )
          ? InDefCommonFlagType.NO
          : InDefCommonFlagType.YES,
        roleCd: roleCd,
        teamCd: teamCd,
      },
      pjtId: params.pjtId,
      pjtTypeCd: PjtType.INTERFACE,
      updateDateTime: pageState.result.updateDateTime,
      isShow: true,
    });

    setInputStakeholderDialogState({
      ...inputStakeholderDialogState,
      saved: { ...addSpreadsheetRow },
      edited: { ...addSpreadsheetRow },
      fromActionList: true,
      requireRow: true,
      rowIdx: rowIdx,
      isShow: true,
      status: {
        ...inputStakeholderDialogState.status,
      },
    });
  };

  const onListenCloseProjectStakeholderList = (): void => {
    if (projectStakeholderListState === undefined) return;
    if (
      projectStakeholderListState.isShow === false &&
      projectStakeholderListState.status.isSaved === true
    )
      onInitialDisplayed();
  };
  useEffect(onListenCloseProjectStakeholderList, [
    projectStakeholderListState.isShow,
  ]);

  const reloadInStakeholderList = (): void => {
    if (projectStakeholderListState.reqReload === false) return;
    getProjectRequest({
      pjtId: params.pjtId,
      dfnSetVerNum: dfnSetVerNum,
    });
    setProjectStakeholderListState({
      ...projectStakeholderListState,
      reqReload: false,
    });
  };
  useEffect(reloadInStakeholderList, [projectStakeholderListState.reqReload]);

  const onListenGetProjectResponseInProjectStakeholderList = () => {
    if (projectStakeholderListState.isShow !== true) return;

    setProjectStakeholderListState({
      ...projectStakeholderListState,
      saved: pageState.result.stakeholderList,
      edited: pageState.result.stakeholderList,
      invalidRoleList: pageState.result.invalidRoleList,
      updateDateTime: pageState.result.updateDateTime,
      status: ProjectStakeholderListInitialState.status,
    });
  };
  useEffect(onListenGetProjectResponseInProjectStakeholderList, [
    pageState.result.stakeholderList,
  ]);

  const createProjectTicketRequest = (crtPjtTcktReq: CrtPjtTcktReq) => {
    const createProjectTicket = () => {
      resetUserError();
      const request: CreateProjectPlanTicketRequest = {
        pjtId: params.pjtId,
        updateDateTimeTPjt: pageState.result.updateDateTime,
      };

      setPageState((prevState) => ({
        ...prevState,
        infoBar: {
          ...prevState.infoBar,
          ready: crtPjtTcktReq.changedMassage,
        },
      }));

      createProjectTicketPlanRequest(request);
    };

    showConfirmationDialog({
      title: "",
      message: crtPjtTcktReq.confirmMessage,
      type: "confirm",
      onOkButtonClicked: createProjectTicket,
    });
  };

  const onListenCreateProjectTicketResponse = (): void => {
    console.log("onListenCreateProjectTicketResponse");
    if (createProjectPlanTicketResponse === undefined) return;

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });

    onInitialDisplayed(pageState.infoBar.ready);
  };
  useEffect(onListenCreateProjectTicketResponse, [
    createProjectPlanTicketResponse,
  ]);

  const updateStatusRequest = (updStsReq: UpdStsReq) => {
    const updateTicketStatus = () => {
      resetUserError();
      if (updStsReq.tcktId === undefined) return;

      let request = {
        evntGrpTypeCd: updStsReq.ok.evntGrpTypeCd,
        tcktId: updStsReq.tcktId,
        dfnSetVerNum: dfnSetVerNum,
        teamCd: TEAM_CODE_PROJECT,
        preProcessParams: updStsReq.ok.preProcessParams,
        postProcessParams: updStsReq.ok.postProcessParams,
      };

      if (updStsReq.ok.preProcessParams === undefined)
        delete request.preProcessParams;
      if (updStsReq.ok.postProcessParams === undefined)
        delete request.postProcessParams;

      setPageState((prevState) => ({
        ...prevState,
        infoBar: {
          ...prevState.infoBar,
          ready: updStsReq.ok.changedMassage,
        },
      }));

      updateTicketRequest(request);
    };

    const updateTicketStatusWithComment = (
      clickOk: boolean,
      comment: string,
    ) => {
      resetUserError();
      if (updStsReq.tcktId === undefined) return;

      const request: UpdateTicketRequest = {
        tcktId: updStsReq.tcktId,
        evntGrpTypeCd: clickOk
          ? updStsReq.ok.evntGrpTypeCd
          : updStsReq.ng?.evntGrpTypeCd,
        teamCd: TEAM_CODE_PROJECT,
        dfnSetVerNum: dfnSetVerNum,
        cmnt:
          comment === "" ? MESSAGE_FOR_CHANGE_STATUS_WITHOUT_COMMENT : comment,
        preProcessParams: clickOk
          ? updStsReq.ok.preProcessParams
          : updStsReq.ng?.preProcessParams,
        postProcessParams: clickOk
          ? updStsReq.ok.postProcessParams
          : updStsReq.ng?.postProcessParams,
      };

      setPageState((prevState) => ({
        ...prevState,
        infoBar: {
          ...prevState.infoBar,
          ready: clickOk
            ? updStsReq.ok.changedMassage
            : (updStsReq.ng?.changedMassage ?? ""),
        },
      }));

      updateTicketRequest(request);
    };

    switch (updStsReq.dialogType) {
      case DialogType.Confirm:
        showConfirmationDialog({
          title: "",
          message: updStsReq.confirmMessage,
          type: "confirm",
          onOkButtonClicked: updateTicketStatus,
        });
        return;
      case DialogType.ConfirmWithComment:
        setConfirmationWithCommentDialogState({
          ...confirmationWithCommentDialogState,
          title: "",
          message: updStsReq.confirmMessage,
          type: "confirm",
          onOkButtonClicked: updateTicketStatusWithComment,
          isShow: true,
        });
        return;
      case DialogType.Judgement:
        setConfirmationWithCommentDialogState({
          ...confirmationWithCommentDialogState,
          title: "",
          message: updStsReq.confirmMessage,
          type: "confirm",
          layout: {
            ...confirmationWithCommentDialogState.layout,
            showNgButton: true,
            okButtonTitle: "APPROVE",
            reqCommClickOk: false,
          },
          onOkButtonClicked: updateTicketStatusWithComment,
          onNgButtonClicked: updateTicketStatusWithComment,
          isShow: true,
        });
    }
  };

  const onListenUpdateStatusResponse = (): void => {
    if (updateTicketResponse === undefined) return;

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });

    onInitialDisplayed(pageState.infoBar.ready);
  };
  useEffect(onListenUpdateStatusResponse, [updateTicketResponse]);

  const uploadIfReqSheet = (event: CustomEvent) => {
    resetUserError();
    const request: UploadRequirementsSheetRequest = {
      pjtId: params.pjtId,
      file: event.detail.file,
      updateDateTime: pageState.result.updateDateTime,
    };
    uploadRequirementsSheetRequest(request);
  };

  const onUploadIfReqSheet = () => {
    if (uploadRequirementsSheetResponse === undefined) return;
    console.log("onUploadIfReqSheet");
    console.log(uploadRequirementsSheetResponse);

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });

    onInitialDisplayed();
  };
  useEffect(onUploadIfReqSheet, [uploadRequirementsSheetResponse]);

  const downloadIfReqSheet = () => {
    resetUserError();
    const request: DownloadRequirementsSheetRequest = {
      pjtId: params.pjtId,
    };
    downloadRequirementsSheetRequest(request);
  };

  const getStakeholders = (roleCd: string) => {
    return pageState.result.stakeholderList
      .filter((e) => e.roleCd === roleCd)
      .map(
        (e) =>
          `${e.primFlg === InDefCommonFlagType.YES ? "*" : ""}${
            [
              RoleType.DIP_MANAGER,
              RoleType.PIC_OF_IF_RD,
              RoleType.PROJECT_OWNER,
              RoleType.PROJECT_APPROVER,
            ].includes(e.roleCd as RoleType)
              ? ""
              : "[" + e.teamCd + "]"
          } ${e.userName} (${e.mlAdrs})`,
      )
      .join(", ");
  };

  const onClickReload = () => {
    resetUserError();
    onInitialDisplayed();
  };

  return (
    <>
      {memoEditDialogState.isShow && (
        <>
          <MemoEditDialogContent />
        </>
      )}
      {pageState.infoBar.isShow && (
        <>
          <FrVrfAlertBar
            style={{ "--vrf-error-color": VRF_SUCCESS_COLOR }}
            text={pageState.infoBar.message}
          />
        </>
      )}
      {confirmationWithCommentDialogState.isShow && (
        <>
          <ConfirmationWithCommentDialogContent />
        </>
      )}
      {inputProjectInformationDialogState.isShow && (
        <>
          <InputProjectInformationDialogContent />
        </>
      )}
      {inputCostEstimationDialogState.isShow && (
        <>
          <InputCostEstimationDialogContent />
        </>
      )}
      {inputComponentRequirementDialogState.isShow && (
        <>
          <InputComponentRequirementDialogContent />
        </>
      )}
      {linkComponentProjectDialogState.isShow && (
        <>
          <LinkComponentProjectDialogContent />
        </>
      )}
      {projectStakeholderListState.isShow ? (
        <>
          <ProjectStakeholderListContent />
        </>
      ) : (
        <>
          <div style={{ paddingBottom: "80px" }}>
            <FrVrfAccordion summary="Progress">
              <ProjectPlanStatusPanel
                planStatusCd={pageState.result.planStatus?.stsId}
                executionStatusCd={pageState.result.executionStatus?.stsId}
                stakeholderList={pageState.result.stakeholderList}
              />
              <ProjectPlanActionList
                pjtId={params.pjtId}
                project={pageState.result}
                showProjectInformationDialog={showProjectInformationDialog}
                showProjectStakeholderList={showProjectStakeholderList}
                showCostEstimationDialog={showCostEstimationDialog}
                showComponentRequirementDialog={showComponentRequirementDialog}
                showComponentProjectDialog={showComponentProjectDialog}
                showAndAddProjectStakeholderList={
                  showAndAddProjectStakeholderList
                }
                uploadIfReqSheetRequest={uploadIfReqSheet}
                createProjectTicketRequest={createProjectTicketRequest}
                updateStatusRequest={updateStatusRequest}
                loginUserRoleList={pageState.loginUserRoleList.map(
                  (e) => e.roleCd,
                )}
              />
            </FrVrfAccordion>
            <FrVrfAccordion summary="Project Plan Summary">
              <FrVrfFormPanel columns={3}>
                <div>
                  <FrVrfPanel panelTitle="Project Information" columns={1}>
                    <div
                      style={{
                        minHeight: "350px",
                        display: "flex",
                        flexFlow: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ marginTop: "20px" }}>
                        <FrVrfSingleLineDisplayField
                          label="Project ID"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={params.pjtId}
                        />
                        <FrVrfSingleLineDisplayField
                          label="Project Name"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.projectInformation.pjtName
                          }
                        />
                        <FrVrfSingleLineDisplayField
                          label="Description"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.projectInformation.pjtDesc
                          }
                        />
                        <FrVrfSingleLineDisplayField
                          label="Scheduled Period"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.projectInformation
                              .pjtStartPlnDate !== undefined
                              ? `${pageState.result.projectInformation.pjtStartPlnDate} - ${pageState.result.projectInformation.pjtEndPlnDate}`
                              : ""
                          }
                        />
                        <FrVrfSingleLineDisplayField
                          label="External Connection"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.projectInformation.trnsMsnTypeName
                          }
                        />
                        <FrVrfSingleLineDisplayField
                          label="EDI Protocol"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.projectInformation
                              .chnTypeGrpTypeCdList === undefined
                              ? ""
                              : pageState.result.projectInformation
                                    .chnTypeGrpTypeCdList.length !== 0
                                ? pageState.result.projectInformation
                                    .chnTypeGrpTypeName
                                : "Do Not Use"
                          }
                        />
                        <FrVrfSingleLineDisplayField
                          label="Due Date for IE Cost Estimation"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.projectInformation
                              .reqEstmtAnsDueDate
                          }
                        />
                      </div>
                      <div>
                        {pageState.authorized && (
                          <FrVrfButtonPanel align="right">
                            <FrVrfButton
                              content="Edit"
                              theme="secondary"
                              size="small"
                              visible={
                                getProjectResponse === undefined ||
                                getProjectResponse.planStatus === null
                              }
                              onClicked={showProjectInformationDialog}
                            />
                            <FrVrfButton
                              content="Edit"
                              theme="secondary"
                              size="small"
                              visible={
                                getProjectResponse !== undefined &&
                                getProjectResponse.planStatus !== null
                              }
                              onClicked={showProjectInformationDialog}
                              authActId="project_plan.button.project_information_edit"
                            />
                          </FrVrfButtonPanel>
                        )}
                      </div>
                    </div>
                  </FrVrfPanel>
                </div>
                <div>
                  <FrVrfPanel
                    panelTitle="IF and Component Requirement Definition"
                    description=""
                    columns={1}
                  >
                    <div
                      style={{
                        minHeight: "350px",
                        display: "flex",
                        flexFlow: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ marginTop: "20px" }}>
                        <FrVrfSingleLineDisplayField
                          label="DIP Test Env Usage"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={pageState.result.tstUseTypeName}
                        />
                        <FrVrfSingleLineDisplayField
                          label="IF Requirements Sheet"
                          style={{
                            "--vrf-single-line-display-field-font-size": "16px",
                            "--vrf-single-line-display-field-label-font-size":
                              "12px",
                          }}
                          firstValue={
                            pageState.result.ifReqSTmpUpdLastDTime
                              ? `Last Uploaded: ${pageState.result.ifReqSTmpUpdLastDTime}`
                              : "Never Uploaded"
                          }
                          secondValue={
                            pageState.result.ifReqSTmpUpdLastDTime
                              ? `(${
                                  Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone
                                })`
                              : ""
                          }
                        />
                        <FrVrfTextDisplayField
                          text="Component Change Requirements"
                          style={{ marginTop: "20px" }}
                        />
                        <div>
                          <table>
                            <thead>
                              <tr>
                                <td style={{ paddingRight: "30px" }}>
                                  <FrVrfTextDisplayField text="DIP" />
                                </td>
                                <td style={{ paddingRight: "30px" }}>
                                  <FrVrfTextDisplayField text="Component" />
                                </td>
                                <td style={{ paddingRight: "30px" }}>
                                  <FrVrfTextDisplayField text="Description" />
                                </td>
                              </tr>
                            </thead>
                            <tbody>
                              {pageState.result.componentRequirementList ===
                              undefined
                                ? null
                                : pageState.result.componentRequirementList.map(
                                    (data, index) => {
                                      return (
                                        <tr
                                          key={`${index}`}
                                          style={{
                                            borderBottomStyle: "solid",
                                            borderBottomWidth: "1px",
                                            borderBottomColor:
                                              VRF_HORIZONTAL_SEPARATOR_COLOR,
                                          }}
                                        >
                                          <td>
                                            <FrVrfTextDisplayField
                                              text={data.dipByRgn}
                                              style={{
                                                "--vrf-text-display-field-font-size":
                                                  "16px",
                                              }}
                                            />
                                          </td>
                                          <td>
                                            <FrVrfTextDisplayField
                                              text={data.cpntId}
                                              style={{
                                                "--vrf-text-display-field-font-size":
                                                  "16px",
                                              }}
                                            />
                                          </td>
                                          <td>
                                            <FrVrfTextDisplayField
                                              text={data.cpntReqDesc}
                                              style={{
                                                "--vrf-text-display-field-font-size":
                                                  "16px",
                                              }}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    },
                                  )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div>
                        {pageState.authorized && (
                          <FrVrfButtonPanel align="right">
                            <FrVrfTextDisplayField text="IF Requirement Sheet" />
                            <FrVrfFileUploadField
                              uploadBtnText="Upload"
                              accept=".xlsx"
                              visible={
                                getProjectResponse !== undefined &&
                                getProjectResponse.planStatus !== null
                              }
                              onFileSelected={uploadIfReqSheet}
                              authActId="project_plan.button.upload"
                            />
                            <FrVrfButton
                              content="Download"
                              theme="secondary"
                              size="small"
                              visible={
                                getProjectResponse !== undefined &&
                                getProjectResponse.planStatus !== null
                              }
                              onClicked={downloadIfReqSheet}
                              authActId="project_plan.button.download"
                            />
                            <FrVrfButton
                              style={{ marginLeft: "30px" }}
                              content="Edit"
                              theme="secondary"
                              size="small"
                              visible={
                                getProjectResponse !== undefined &&
                                getProjectResponse.planStatus !== null
                              }
                              onClicked={showComponentRequirementDialog}
                              authActId="project_plan.button.if_and_component_requirement_definition_edit"
                            />
                          </FrVrfButtonPanel>
                        )}
                      </div>
                    </div>
                  </FrVrfPanel>
                </div>
                <div>
                  <div>
                    <FrVrfPanel
                      panelTitle="Cost Estimation"
                      description=""
                      columns={1}
                    >
                      <div
                        style={{
                          minHeight: "100px",
                          display: "flex",
                          flexFlow: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ marginTop: "20px" }}>
                          <FrVrfSingleLineDisplayField
                            label="Necessity of Securing new Budget"
                            style={{
                              "--vrf-single-line-display-field-font-size":
                                "16px",
                              "--vrf-single-line-display-field-label-font-size":
                                "12px",
                              "--vrf-single-line-display-field-label-width":
                                "350px",
                            }}
                            firstValue={
                              pageState.result.costEstimation
                                .pjtBdgtAcqstnReqTypeName
                            }
                          />
                          <FrVrfSingleLineDisplayField
                            label="Consensus of budget usage between project team and IE team"
                            style={{
                              "--vrf-single-line-display-field-font-size":
                                "16px",
                              "--vrf-single-line-display-field-label-font-size":
                                "12px",
                              "--vrf-single-line-display-field-label-width":
                                "350px",
                            }}
                            firstValue={
                              pageState.result.costEstimation
                                .statsOfAgreementOnBudgetaryTypeName
                            }
                          />
                        </div>
                        <div>
                          {pageState.authorized && (
                            <FrVrfButtonPanel align="right">
                              <FrVrfButton
                                style={{ marginLeft: "30px" }}
                                content="Edit"
                                theme="secondary"
                                size="small"
                                visible={
                                  getProjectResponse !== undefined &&
                                  getProjectResponse.planStatus !== null
                                }
                                onClicked={showCostEstimationDialog}
                                authActId="project_plan.button.cost_estimation_edit"
                              />
                            </FrVrfButtonPanel>
                          )}
                        </div>
                      </div>
                    </FrVrfPanel>
                  </div>
                  <div style={{ height: "10px" }} />
                  <div>
                    <FrVrfPanel
                      panelTitle="Linked Component Project"
                      description=""
                      columns={1}
                    >
                      <div
                        style={{
                          minHeight: "180px",
                          display: "flex",
                          flexFlow: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ marginTop: "10px" }}>
                          <table>
                            <thead>
                              <tr>
                                <td style={{ paddingRight: "30px" }}>
                                  <FrVrfTextDisplayField text="Project Name" />
                                </td>
                                <td style={{ paddingRight: "30px" }}>
                                  <FrVrfTextDisplayField text="Status" />
                                </td>
                              </tr>
                            </thead>
                            <tbody>
                              {pageState.result.componentProjectList ===
                              undefined
                                ? null
                                : pageState.result.componentProjectList.map(
                                    (data, index) => {
                                      return (
                                        <tr
                                          key={`${index}`}
                                          style={{
                                            borderBottomStyle: "solid",
                                            borderBottomWidth: "1px",
                                            borderBottomColor:
                                              VRF_HORIZONTAL_SEPARATOR_COLOR,
                                          }}
                                        >
                                          <td>
                                            <FrVrfTextDisplayField
                                              text={data.pjtName}
                                              style={{
                                                "--vrf-text-display-field-font-size":
                                                  "16px",
                                              }}
                                            />
                                          </td>
                                          <td>
                                            <FrVrfTextDisplayField
                                              text={data.pjtStsDsplyName}
                                              style={{
                                                "--vrf-text-display-field-font-size":
                                                  "16px",
                                              }}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    },
                                  )}
                            </tbody>
                          </table>
                        </div>
                        <div>
                          {pageState.authorized && (
                            <FrVrfButtonPanel align="right">
                              <FrVrfButton
                                style={{ marginLeft: "30px" }}
                                content="Edit"
                                theme="secondary"
                                size="small"
                                visible={
                                  getProjectResponse !== undefined &&
                                  getProjectResponse.planStatus !== null
                                }
                                onClicked={showComponentProjectDialog}
                                authActId="project_plan.button.linked_component_project_edit"
                              />
                            </FrVrfButtonPanel>
                          )}
                        </div>
                      </div>
                    </FrVrfPanel>
                  </div>
                </div>
              </FrVrfFormPanel>
            </FrVrfAccordion>
            <FrVrfAccordion summary="Stakeholders">
              <FrVrfPanel
                description=""
                columns={1}
                style={{ minHeight: "100px" }}
              >
                <FrVrfSingleLineDisplayField
                  label="Project Owner"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.PROJECT_OWNER)}
                />
                <FrVrfHorizontalSeparator />
                <FrVrfSingleLineDisplayField
                  label="Project Approver"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.PROJECT_APPROVER)}
                />
                <FrVrfHorizontalSeparator />
                <FrVrfSingleLineDisplayField
                  label="DIP Manager"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.DIP_MANAGER)}
                />
                <FrVrfHorizontalSeparator />
                <FrVrfSingleLineDisplayField
                  label="DIP Approver"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.DIP_APPROVER)}
                />
                <FrVrfHorizontalSeparator />
                <FrVrfSingleLineDisplayField
                  label="PIC of IF Requirement"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.PIC_OF_IF_RD)}
                />
                <FrVrfHorizontalSeparator />
                <FrVrfSingleLineDisplayField
                  label="PIC of DIP App"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.PIC_OF_DIP_APP)}
                />
                <FrVrfHorizontalSeparator />
                <FrVrfSingleLineDisplayField
                  label="PIC of Component App"
                  style={{
                    "--vrf-single-line-display-field-font-size": "16px",
                    "--vrf-single-line-display-field-label-font-size": "12px",
                    "--vrf-single-line-display-field-label-width": "140px",
                  }}
                  firstValue={getStakeholders(RoleType.PIC_OF_COMPONENT_APP)}
                />
                {pageState.authorized && (
                  <FrVrfButtonPanel align="right">
                    <FrVrfButton
                      content="Edit"
                      theme="secondary"
                      size="small"
                      visible={
                        getProjectResponse === undefined ||
                        getProjectResponse.planStatus === null
                      }
                      onClicked={showProjectStakeholderList}
                    />
                    <FrVrfButton
                      content="Edit"
                      theme="secondary"
                      size="small"
                      visible={
                        getProjectResponse !== undefined &&
                        getProjectResponse.planStatus !== null
                      }
                      onClicked={showProjectStakeholderList}
                      authActId="project_plan.button.stakeholders_edit"
                    />
                  </FrVrfButtonPanel>
                )}
              </FrVrfPanel>
            </FrVrfAccordion>
            {/* Project Memo Area */}
            <FrVrfAccordion
              summary="Memo"
              visible={getProjectResponse !== undefined}
            >
              <FrVrfPanel
                description=""
                columns={1}
                style={{ minHeight: "100px" }}
              >
                <FrVrfTextDisplayField
                  className="whiteSpace"
                  text={pageState.result.mmTxt}
                />
                <FrVrfButtonPanel align="right">
                  <FrVrfButton
                    style={{ marginLeft: "30px" }}
                    content="Edit"
                    theme="secondary"
                    size="small"
                    visible={getProjectResponse !== undefined}
                    onClicked={showMemoEditDialog}
                  />
                </FrVrfButtonPanel>
              </FrVrfPanel>
            </FrVrfAccordion>

            {pageState.authorized && (
              <FrVrfFooter>
                <FrVrfButton
                  content="Reload"
                  size="large"
                  onClicked={onClickReload}
                  authActId="project_plan.button.reload"
                />
              </FrVrfFooter>
            )}
          </div>
        </>
      )}
    </>
  );
};
